import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useInfiniteQuery, useQuery } from '@tanstack/vue-query'

import type { IUpsellOffer } from '@courses/types-layer/types/offer'
import type { LibraryProducts } from '@courses/types-layer/types/library'
import { LibraryService } from '../services/library.service'
import type { DiscoverLibraryItem, DiscoverProductsResponse, UpsellProductsResponse } from '../types/library'

export const useLibraryQueryStore = (locationId: MaybeRefOrGetter<string>) => {
  const service = new LibraryService()

  const queryKeys = {
    all: (locId?: string) => ['library', locId] as const,
    list: (locId?: string) => ['library', locId, 'list'] as const,
    upsell: (locId?: string, limit?: number) => ['library', locId, 'upsell', limit] as const,
    discover: (locId?: string, limit?: number) => ['library', locId, 'discover', limit] as const,
  }

  const fetchAllProductsQuery = () => {
    return useQuery<LibraryProducts, Error>({
      queryKey: computed(() => queryKeys.list(toValue(locationId))),
      queryFn: async () => await service.findAllProducts(toValue(locationId)),
      // Always stale: access can be revoked server-side (e.g. workflows), so My Courses / Dashboard
      // refetch on mount and window focus instead of showing a revoked course for up to 5 minutes.
      staleTime: 0,
      enabled: computed(() => !!toValue(locationId)),
    })
  }

  const fetchUpsellProductsQuery = (params: { limit: number }) => {
    const infiniteQuery = useInfiniteQuery<UpsellProductsResponse, Error>({
      queryKey: computed(() => queryKeys.upsell(toValue(locationId), params.limit)),
      queryFn: ({ pageParam = 1 }) =>
        service.findUpsellProducts(toValue(locationId), {
          page: pageParam as number,
          limit: params.limit,
        }),
      getNextPageParam: lastPage => {
        const totalFetched = (lastPage.page - 1) * params.limit + lastPage.items.length
        return totalFetched < lastPage.total ? lastPage.page + 1 : undefined
      },
      initialPageParam: 1,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId)),
      refetchOnWindowFocus: false,
    })

    const allData = computed<IUpsellOffer[]>(() => infiniteQuery.data.value?.pages?.flatMap(p => p.items) ?? [])

    return { ...infiniteQuery, allData }
  }

  /**
   * Discover feed. Intentionally a sibling of `fetchUpsellProductsQuery` rather than a variant of
   * it: same page/limit envelope, different item shape, and the two are consumed by different
   * screens — collapsing them would couple the Discover page to the home-page upsell strip.
   */
  const fetchDiscoverProductsQuery = (params: { limit: number }) => {
    const infiniteQuery = useInfiniteQuery<DiscoverProductsResponse, Error>({
      queryKey: computed(() => queryKeys.discover(toValue(locationId), params.limit)),
      queryFn: ({ pageParam = 1 }) =>
        service.findDiscoverProducts(toValue(locationId), {
          page: pageParam as number,
          limit: params.limit,
        }),
      getNextPageParam: lastPage => {
        const totalFetched = (lastPage.page - 1) * params.limit + lastPage.items.length
        return totalFetched < lastPage.total ? lastPage.page + 1 : undefined
      },
      initialPageParam: 1,
      // Always stale: cached pages render instantly, then refetch on mount so offers made
      // discoverable in the builder show up on the next visit. The backend invalidates its cache
      // on change and answers unchanged pages with an ETag, so the refetch stays cheap.
      staleTime: 0,
      gcTime: 10 * 60 * 1000,
      enabled: computed(() => !!toValue(locationId)),
      refetchOnWindowFocus: false,
    })

    const allData = computed<DiscoverLibraryItem[]>(() => infiniteQuery.data.value?.pages?.flatMap(p => p.items) ?? [])

    return { ...infiniteQuery, allData }
  }

  return {
    queryKeys,
    fetchAllProductsQuery,
    fetchUpsellProductsQuery,
    fetchDiscoverProductsQuery,
  }
}
